import React, { useEffect, useState } from "react";

type LazyImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
};

/**
 * Renders an <img> whose `src` is only set after hydration.
 *
 * Why: several project/brand images are stored as base64 data URIs. If they
 * are server-rendered with `src="data:..."`, ~1.9 MB of base64 gets inlined
 * into the initial HTML document (the single biggest Lighthouse penalty on
 * this site). None of these images are the LCP element (the hero background
 * is a hosted CDN URL), so deferring their `src` to after mount keeps the
 * SSR HTML tiny while preserving layout (width/height are still emitted)
 * and SEO (alt text is still emitted). No hydration mismatch: both the
 * server and the first client render omit `src`, then `useEffect` flips it on.
 */
export const LazyImg: React.FC<LazyImgProps> = ({ src, alt, ...rest }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  return <img src={ready ? src : undefined} alt={alt} {...rest} />;
};
